class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    this.formId = this.dataset.productFormId;
    this.sectionId = this.dataset.section;
    this.mainForm = this.formId ? document.getElementById(this.formId) : null;
    this.addBtn = this.querySelector(".js-sticky-add");
    this.qtyInput = this.querySelector(".js-counter-quantity");
    this.selects = Array.from(this.querySelectorAll(".wt-sticky-buy__select"));

    this.wireControls();
    this.wireVisibilityObserver();
  }

  wireControls() {
    // Variant dropdowns drive the main product form's variant picker so all
    // existing variant logic (price, gallery, availability) stays the source of truth.
    this.selects.forEach((select) => {
      select.addEventListener("change", () => this.onSelectChange(select));
    });

    // Add to cart reuses the main <product-form> submit flow (cart drawer, errors).
    this.addBtn?.addEventListener("click", () => this.onAddToCart());

    // Keep the sticky price + button in sync when the variant changes anywhere.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromMainForm());
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setLoading(false));
      subscribe(PUB_SUB_EVENTS.cartError, () => this.setLoading(false));
    }

    this.syncButtonState();
  }

  onSelectChange(select) {
    if (!this.formId) return;
    const name = select.dataset.optionName;
    const value = select.value;
    const radio = document.querySelector(
      `input[type="radio"][form="${this.formId}"][name="${CSS.escape(
        name,
      )}"][value="${CSS.escape(value)}"]`,
    );
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // Mirror the current variant's price + button state into the sticky bar, and
  // keep the sticky dropdowns aligned with the main picker.
  syncFromMainForm() {
    const sourcePrice = document.getElementById(`price-${this.sectionId}`);
    const stickyPrice = document.getElementById(`StickyPrice-${this.sectionId}`);
    if (sourcePrice && stickyPrice) stickyPrice.innerHTML = sourcePrice.innerHTML;

    this.selects.forEach((select) => {
      const checked = document.querySelector(
        `input[type="radio"][form="${this.formId}"][name="${CSS.escape(
          select.dataset.optionName,
        )}"]:checked`,
      );
      if (checked && select.value !== checked.value) select.value = checked.value;
    });

    this.syncButtonState();
  }

  syncButtonState() {
    if (!this.mainForm || !this.addBtn) return;
    const mainBtn = this.mainForm.querySelector('[name="add"]');
    if (!mainBtn) return;
    const disabled =
      mainBtn.hasAttribute("disabled") ||
      mainBtn.getAttribute("aria-disabled") === "true";
    this.addBtn.toggleAttribute("disabled", disabled);
    const label = this.addBtn.querySelector("span");
    const mainLabel = mainBtn.querySelector("span");
    if (label && mainLabel) label.textContent = mainLabel.textContent.trim();
  }

  onAddToCart() {
    if (!this.mainForm || this.addBtn.hasAttribute("disabled")) return;

    const qty = parseInt(this.qtyInput?.value, 10) || 1;
    let formQty = this.mainForm.querySelector('input[name="quantity"]');
    if (!formQty) {
      formQty = document.createElement("input");
      formQty.type = "hidden";
      formQty.name = "quantity";
      this.mainForm.appendChild(formQty);
    }
    formQty.value = qty;

    this.setLoading(true);
    if (typeof this.mainForm.requestSubmit === "function") {
      this.mainForm.requestSubmit();
    } else {
      this.mainForm.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  }

  setLoading(isLoading) {
    if (!this.addBtn) return;
    this.addBtn.classList.toggle("loading", isLoading);
    this.addBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    const spinner = this.addBtn.querySelector(".loading-overlay__spinner");
    if (spinner) spinner.classList.toggle("hidden", !isLoading);
  }

  wireVisibilityObserver() {
    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
